import { useState, useEffect, useRef } from 'react'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@creanizer/ui'
import { Alert, AlertDescription, Button } from '@creanizer/ui'
import { AlertCircle, Upload, FileText, X, Copy, Download, Image, Search, Music } from 'lucide-react'
import { readMultipleFiles, FILE_ACCEPT, formatFileSize } from '../../utils/fileReader'
import { useSettingsStore } from '../../../../stores/settingsStore'
import { useConsultationStore } from '../../../../stores/consultationStore'
import { useConsultation } from '../../hooks/useConsultation'
import { ChatContainer } from './ChatContainer'
import { ChatInput } from './ChatInput'
import { SessionManager } from './SessionManager'
import { UserSettings } from './UserSettings'
import { PersonaSelector } from './PersonaSelector'
import { GeneratorContext } from './GeneratorContext'
import { ConsultationSettings } from './ConsultationSettings'
import { ConferenceSelector } from './ConferenceSelector'
import type { ConferenceSettings } from '@creanizer/types'

export function ConsultationTool() {
  const { isApiKeySet } = useSettingsStore()
  const consultationStore = useConsultationStore()
  const activeSession = useConsultationStore(state => state.activeSession) // 直接セレクタを使用
  const isHydrated = useConsultationStore(state => state.isHydrated) // hydration状態を取得
  const consultation = useConsultation()
  const [activeTab, setActiveTab] = useState('chat')
  const [isStreaming, setIsStreaming] = useState(false)
  const [generatorContext, setGeneratorContext] = useState<any>(null)
  const [criticalMode, setCriticalMode] = useState(false)
  const [customContext, setCustomContext] = useState<string>('')  // カスタム設定
  const [avoidElements, setAvoidElements] = useState<string[]>([])  // 禁止ワード
  const [uploadedFiles, setUploadedFiles] = useState<Array<{ id: string; name: string; content: string; size: number }>>([])  // アップロードファイル管理
  const [uploadedImages, setUploadedImages] = useState<Array<{ id: string; name: string; data: string; size: number }>>([])  // アップロード画像管理
  const [useGrounding, setUseGrounding] = useState(false)  // Google検索による情報補強
  const fileInputRef = useRef<HTMLInputElement>(null)  // ファイル入力への参照
  const imageInputRef = useRef<HTMLInputElement>(null)  // 画像入力への参照
  const [showSummary, setShowSummary] = useState(false)  // まとめ表示の状態
  const [currentSummary, setCurrentSummary] = useState<{ summary: string; messageCount: number; timeRange: { start: string; end: string } } | null>(null)
  
  // 会議設定はstoreから取得
  const conferenceSettings = consultationStore.conferenceSettings
  const setConferenceSettings = (settings: ConferenceSettings | null) => {
    consultationStore.setConferenceSettings(settings)
  }
  
  // 初回起動時にセッションを作成
  useEffect(() => {
    // hydration完了まで待つ
    if (!isHydrated) return

    if (!activeSession && consultationStore.sessions.length === 0) {
      consultationStore.createSession('新しい相談')
    }
  }, [isHydrated, activeSession, consultationStore.sessions.length, consultationStore])
  
  // アクティブセッションが変更されたらチャットタブに切り替え
  useEffect(() => {
    if (activeSession) {
      setActiveTab('chat')
    }
  }, [activeSession?.id])
  
  const handleSendMessage = async (message: string) => {
    if (!isApiKeySet()) {
      consultation.clearError()
      return
    }

    // カスタムコンテキストを含める
    const finalContext = {
      ...generatorContext,
      customContext: customContext || undefined
    }

    // 画像データを準備
    const imageData = uploadedImages.length > 0
      ? uploadedImages.map(img => img.data)
      : undefined

    setIsStreaming(true)
    try {
      await consultation.sendMessage(message, {
        images: imageData,
        context: finalContext,
        criticalMode,
        useGrounding,
        avoidElements: avoidElements.length > 0 ? avoidElements : undefined,
        personaSettings: conferenceSettings?.enabled ? undefined : consultationStore.personaSettings || undefined,
        conferenceSettings: conferenceSettings?.enabled ? conferenceSettings : undefined
      })

      // 送信後に画像をクリア
      if (uploadedImages.length > 0) {
        setUploadedImages([])
      }
    } finally {
      setIsStreaming(false)
    }
  }
  
  
  const handleRetryUserMessage = async (messageId: string) => {
    if (!isApiKeySet()) {
      consultation.clearError()
      return
    }
    
    // カスタムコンテキストを含める
    const finalContext = {
      ...generatorContext,
      customContext: customContext || undefined
    }
    
    setIsStreaming(true)
    try {
      // ユーザーメッセージを再送信（後続メッセージは削除）
      await consultation.retryUserMessage(messageId, {
        context: finalContext,
        criticalMode,
        useGrounding,
        avoidElements: avoidElements.length > 0 ? avoidElements : undefined,
        personaSettings: conferenceSettings?.enabled ? undefined : consultationStore.personaSettings || undefined,
        conferenceSettings: conferenceSettings?.enabled ? conferenceSettings : undefined
      })
    } finally {
      setIsStreaming(false)
    }
  }
  
  const handleRegenerateAIMessage = async (messageId: string) => {
    if (!isApiKeySet()) {
      consultation.clearError()
      return
    }
    
    // カスタムコンテキストを含める
    const finalContext = {
      ...generatorContext,
      customContext: customContext || undefined
    }
    
    setIsStreaming(true)
    try {
      // AIメッセージを再生成（後続メッセージは削除）
      await consultation.regenerateAIMessage(messageId, {
        context: finalContext,
        criticalMode,
        useGrounding,
        avoidElements: avoidElements.length > 0 ? avoidElements : undefined,
        personaSettings: conferenceSettings?.enabled ? undefined : consultationStore.personaSettings || undefined,
        conferenceSettings: conferenceSettings?.enabled ? conferenceSettings : undefined
      })
    } finally {
      setIsStreaming(false)
    }
  }
  
  const handleFileImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files || files.length === 0) return
    
    try {
      const { combinedText, fileInfos, errors } = await readMultipleFiles(files)
      
      // エラーがある場合は表示
      if (errors.length > 0) {
        alert(`以下のファイルの読み込みに失敗しました:\n${errors.join('\n')}`)
      }
      
      // 成功したファイルを追加
      const newFiles = fileInfos
        .filter(info => !info.error && info.text)
        .map(info => ({
          id: `${Date.now()}_${Math.random().toString(36).slice(2, 11)}`,
          name: info.fileName,
          content: info.text,
          size: info.text.length
        }))
      
      setUploadedFiles(prev => [...prev, ...newFiles])
      
      // カスタムコンテキストを更新
      const allFilesContent = [...uploadedFiles, ...newFiles]
        .map(file => `【ファイル: ${file.name}】\n${file.content}`)
        .join('\n\n')
      setCustomContext(allFilesContent)
    } catch (error) {
      console.error('ファイル読み込みエラー:', error)
      alert('ファイルの読み込みに失敗しました')
    }
    
    // ファイル選択をリセット
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }
  
  const handleFileRemove = (fileId: string) => {
    const newFiles = uploadedFiles.filter(f => f.id !== fileId)
    setUploadedFiles(newFiles)

    // カスタムコンテキストを更新
    const allFilesContent = newFiles
      .map(file => `【ファイル: ${file.name}】\n${file.content}`)
      .join('\n\n')
    setCustomContext(allFilesContent)
  }

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files || files.length === 0) return

    const maxSize = 20 * 1024 * 1024 // 20MB制限（音声ファイル対応）
    const validTypes = [
      // 画像
      'image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp',
      // 音声
      'audio/mpeg', 'audio/mp3', 'audio/wav', 'audio/flac', 'audio/aac', 'audio/ogg', 'audio/aiff'
    ]

    const newImages: Array<{ id: string; name: string; data: string; size: number }> = []

    for (const file of Array.from(files)) {
      if (!validTypes.includes(file.type)) {
        alert(`${file.name} はサポートされていない形式です（画像: JPEG/PNG/GIF/WebP、音声: MP3/WAV/FLAC/AAC/OGG）`)
        continue
      }

      if (file.size > maxSize) {
        alert(`${file.name} はファイルサイズが大きすぎます (最大20MB)`)
        continue
      }

      try {
        const reader = new FileReader()
        const base64 = await new Promise<string>((resolve, reject) => {
          reader.onload = () => resolve(reader.result as string)
          reader.onerror = reject
          reader.readAsDataURL(file)
        })

        newImages.push({
          id: `img_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`,
          name: file.name,
          data: base64,
          size: file.size
        })
      } catch (error) {
        console.error('画像読み込みエラー:', error)
        alert(`${file.name} の読み込みに失敗しました`)
      }
    }

    if (newImages.length > 0) {
      setUploadedImages(prev => [...prev, ...newImages].slice(0, 5)) // 最大5枚まで
    }

    // input要素をリセット
    if (imageInputRef.current) {
      imageInputRef.current.value = ''
    }
  }

  const handleImageRemove = (imageId: string) => {
    setUploadedImages(prev => prev.filter(img => img.id !== imageId))
  }
  
  if (!isApiKeySet()) {
    return (
      <div className="container mx-auto p-6">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            APIキーが設定されていません。設定画面でGemini APIキーを入力してください。
          </AlertDescription>
        </Alert>
      </div>
    )
  }
  
  return (
    <div className="container mx-auto p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold mb-2">相談ツール</h1>
        <p className="text-muted-foreground mb-4">
          創作活動の相談を、AIまたはキャラクターと行うチャット形式のツール
        </p>
      </div>
      
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList>
          <TabsTrigger value="chat">チャット</TabsTrigger>
          <TabsTrigger value="sessions">
            セッション ({consultationStore.sessions.length})
          </TabsTrigger>
          <TabsTrigger value="settings">設定</TabsTrigger>
        </TabsList>
        
        <TabsContent value="chat" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
            {/* メインチャットエリア */}
            <div className="lg:col-span-3 space-y-4">
              <div className="card h-[600px] flex flex-col">
                {/* チャット表示 */}
                <ChatContainer
                  messages={activeSession?.messages || []}
                  sessionId={activeSession?.id || ''}
                  isStreaming={isStreaming}
                  onRetryUserMessage={handleRetryUserMessage}
                  onRegenerateAIMessage={handleRegenerateAIMessage}
                />

                {/* メディアアップロードエリア（画像/音声） */}
                {uploadedImages.length > 0 && (
                  <div className="border-t p-3 bg-gray-50">
                    <div className="flex items-center gap-2 flex-wrap">
                      {uploadedImages.map((media) => {
                        const isAudio = media.data.startsWith('data:audio/')
                        return (
                          <div key={media.id} className="relative group">
                            {isAudio ? (
                              <div className="h-20 w-32 flex flex-col items-center justify-center bg-gray-100 rounded border p-1">
                                <Music className="h-6 w-6 text-gray-500 mb-1" />
                                <span className="text-xs text-gray-600 truncate w-full text-center px-1">{media.name}</span>
                                <audio src={media.data} controls className="w-full h-6 mt-1" />
                              </div>
                            ) : (
                              <img
                                src={media.data}
                                alt={media.name}
                                className="h-20 w-20 object-cover rounded border"
                              />
                            )}
                            <button
                              onClick={() => handleImageRemove(media.id)}
                              className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </div>
                        )
                      })}
                      <div className="text-xs text-muted-foreground">
                        {uploadedImages.length}/5 件
                      </div>
                    </div>
                  </div>
                )}

                {/* 入力エリア */}
                <ChatInput
                  onSend={handleSendMessage}
                  disabled={isStreaming || consultation.isLoading}
                />

                {/* 画像アップロードボタンとGrounding検索 */}
                <div className="border-t px-4 py-2 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Button
                      onClick={() => imageInputRef.current?.click()}
                      variant="ghost"
                      size="sm"
                      disabled={uploadedImages.length >= 5 || isStreaming}
                    >
                      <Image className="h-4 w-4 mr-2" />
                      メディアを追加 ({uploadedImages.length}/5)
                    </Button>
                    <input
                      ref={imageInputRef}
                      type="file"
                      accept="image/jpeg,image/jpg,image/png,image/gif,image/webp,audio/mpeg,audio/mp3,audio/wav,audio/flac,audio/aac,audio/ogg,audio/aiff"
                      multiple
                      onChange={handleImageUpload}
                      className="hidden"
                    />
                    <span className="text-xs text-muted-foreground">
                      画像/音声 (最大20MB)
                    </span>
                  </div>

                  {/* Google Grounding検索のチェックボックス */}
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="grounding-search"
                      checked={useGrounding}
                      onChange={(e) => setUseGrounding(e.target.checked)}
                      className="rounded border-gray-300"
                      disabled={isStreaming}
                    />
                    <label
                      htmlFor="grounding-search"
                      className="flex items-center gap-1 text-sm cursor-pointer"
                    >
                      <Search className="h-4 w-4" />
                      Google検索を使用
                    </label>
                  </div>
                </div>
              </div>
              
              {consultation.error && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{consultation.error}</AlertDescription>
                </Alert>
              )}
            </div>
            
            {/* サイドバー: 設定 */}
            <div className="space-y-4">
              {/* ユーザー設定 */}
              <div className="card">
                <h3 className="text-sm font-semibold mb-3">ユーザー設定</h3>
                <UserSettings />
              </div>
              
              {/* ペルソナ選択 */}
              <div className="card">
                <h3 className="text-sm font-semibold mb-3">ペルソナ設定</h3>
                {conferenceSettings?.enabled ? (
                  <div className="text-xs text-muted-foreground">
                    会議モードが有効なため、個別ペルソナは無効です
                  </div>
                ) : (
                  <PersonaSelector />
                )}
              </div>
              
              {/* 会議モード */}
              <div className="card">
                <h3 className="text-sm font-semibold mb-3">会議モード</h3>
                <ConferenceSelector 
                  onSettingsChange={setConferenceSettings}
                  conferenceSettings={conferenceSettings}
                />
              </div>
              
              {/* ジェネレータコンテキスト */}
              <div className="card">
                <h3 className="text-sm font-semibold mb-3">コンテキスト設定</h3>
                <GeneratorContext
                  onContextChange={setGeneratorContext}
                  criticalMode={criticalMode}
                  onCriticalModeChange={setCriticalMode}
                  customContext={customContext}
                  onCustomContextChange={setCustomContext}
                  avoidElements={avoidElements}
                  onAvoidElementsChange={setAvoidElements}
                />
                <div className="mt-2">
                  <Button
                    onClick={() => fileInputRef.current?.click()}
                    variant="outline"
                    size="sm"
                    className="w-full"
                  >
                    <Upload className="h-3 w-3 mr-2" />
                    ファイルをインポート
                  </Button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept={FILE_ACCEPT}
                    multiple
                    onChange={handleFileImport}
                    className="hidden"
                  />
                  
                  {/* アップロード済みファイルのリスト */}
                  {uploadedFiles.length > 0 && (
                    <div className="mt-2 p-2 bg-gray-50 rounded-lg border">
                      <div className="text-xs font-medium text-gray-700 mb-1">読み込み済み：</div>
                      <div className="space-y-1">
                        {uploadedFiles.map((file) => (
                          <div key={file.id} className="flex items-center justify-between bg-white p-1 rounded border">
                            <div className="flex items-center gap-1">
                              <FileText className="h-3 w-3 text-gray-500" />
                              <span className="text-xs">{file.name}</span>
                              <span className="text-xs text-gray-500">({formatFileSize(file.size)})</span>
                            </div>
                            <Button
                              type="button"
                              size="sm"
                              variant="ghost"
                              onClick={() => handleFileRemove(file.id)}
                              className="p-0.5 h-5 w-5"
                            >
                              <X className="h-3 w-3" />
                            </Button>
                          </div>
                        ))}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        合計 {uploadedFiles.length} ファイル
                      </div>
                    </div>
                  )}
                  <p className="text-xs text-muted-foreground mt-1">
                    対応形式: .txt, .md, .docx, .pdf, .json
                  </p>
                </div>
              </div>
              
              {/* セッション操作 */}
              <div className="card">
                <h3 className="text-sm font-semibold mb-3">セッション操作</h3>
                <div className="space-y-2">
                  <button
                    onClick={() => consultationStore.createSession()}
                    className="w-full px-3 py-2 text-sm bg-primary text-primary-foreground rounded hover:opacity-90"
                  >
                    新規セッション
                  </button>
                  {activeSession && (
                    <>
                      <button
                        onClick={async () => {
                          if (!activeSession) return
                          
                          // メッセージ数をチェック
                          if (activeSession.messages.length <= 21) {
                            alert('履歴圧縮には最低21件のメッセージが必要です。')
                            return
                          }
                          
                          const confirmed = confirm(
                            `現在${activeSession.messages.length}件のメッセージがあります。\n` +
                            `直近の10往復（20メッセージ）を残して、それ以前の会話を要約します。\n` +
                            `続行しますか？`
                          )
                          
                          if (confirmed) {
                            try {
                              setIsStreaming(true)
                              await consultationStore.compressSessionHistory(activeSession.id, 20)
                              alert('履歴の圧縮が完了しました。')
                            } catch (error) {
                              console.error('履歴圧縮エラー:', error)
                              alert(error instanceof Error ? error.message : '履歴圧縮に失敗しました')
                            } finally {
                              setIsStreaming(false)
                            }
                          }
                        }}
                        disabled={isStreaming || !activeSession || activeSession.messages.length <= 21}
                        className="w-full px-3 py-2 text-sm bg-secondary text-secondary-foreground rounded hover:opacity-90 disabled:opacity-50"
                      >
                        履歴を圧縮（{activeSession.messages.length}件→約21件）
                      </button>
                      <button
                        onClick={async () => {
                          if (!activeSession) return
                          
                          // メッセージ数をチェック
                          if (activeSession.messages.length === 0) {
                            alert('まとめを作成する会話がありません。')
                            return
                          }
                          
                          const confirmed = confirm(
                            `現在${activeSession.messages.length}件のメッセージがあります。\n` +
                            `会話履歴を削除せずに、議事録形式のまとめを生成します。\n` +
                            `続行しますか？`
                          )
                          
                          if (confirmed) {
                            try {
                              setIsStreaming(true)
                              const summary = await consultationStore.generateSessionSummary(activeSession.id)
                              setCurrentSummary(summary)
                              setShowSummary(true)
                            } catch (error) {
                              console.error('まとめ生成エラー:', error)
                              alert(error instanceof Error ? error.message : 'まとめ生成に失敗しました')
                            } finally {
                              setIsStreaming(false)
                            }
                          }
                        }}
                        disabled={isStreaming || !activeSession || activeSession.messages.length === 0}
                        className="w-full px-3 py-2 text-sm bg-secondary text-secondary-foreground rounded hover:opacity-90 disabled:opacity-50"
                      >
                        まとめを生成（{activeSession.messages.length}件）
                      </button>
                      <button
                        onClick={() => consultationStore.exportSession(activeSession.id)}
                        className="w-full px-3 py-2 text-sm bg-secondary text-secondary-foreground rounded hover:opacity-90"
                      >
                        セッションをエクスポート
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </TabsContent>
        
        <TabsContent value="sessions">
          <SessionManager />
        </TabsContent>
        
        <TabsContent value="settings">
          <div className="card">
            <h3 className="text-lg font-semibold mb-4">相談ツール設定</h3>
            <ConsultationSettings />
          </div>
        </TabsContent>
      </Tabs>
      
      {/* まとめ表示モーダル */}
      {showSummary && currentSummary && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4 overflow-y-auto">
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-4xl max-h-[90vh] md:max-h-[85vh] flex flex-col my-auto">
            {/* モーダルヘッダー */}
            <div className="border-b border-gray-200 px-4 md:px-6 py-3 md:py-4 flex items-center justify-between">
              <div className="flex-1 pr-2">
                <h2 className="text-lg md:text-xl font-semibold text-gray-900">会話のまとめ（議事録）</h2>
                <p className="text-xs md:text-sm text-gray-600 mt-1">
                  {currentSummary.messageCount}件のメッセージ
                  {currentSummary.timeRange.start && currentSummary.timeRange.end && (
                    <span>
                      （{new Date(currentSummary.timeRange.start).toLocaleString('ja-JP', {
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit'
                      })} ～ {new Date(currentSummary.timeRange.end).toLocaleString('ja-JP', {
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}）
                    </span>
                  )}
                </p>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setShowSummary(false)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            
            {/* モーダルコンテンツ */}
            <div className="flex-1 overflow-y-auto px-4 md:px-6 py-4 md:py-6 bg-gray-50">
              <div className="prose prose-base md:prose-lg max-w-none whitespace-pre-wrap text-gray-900 leading-relaxed">
                <div className="bg-white p-4 md:p-6 rounded-lg shadow-inner border border-gray-100">
                  {currentSummary.summary}
                </div>
              </div>
            </div>
            
            {/* モーダルフッター */}
            <div className="border-t border-gray-200 px-4 md:px-6 py-3 md:py-4 flex flex-col sm:flex-row gap-2 sm:justify-end">
              <Button
                variant="outline"
                size="sm"
                className="w-full sm:w-auto"
                onClick={() => {
                  navigator.clipboard.writeText(currentSummary.summary)
                  alert('まとめをクリップボードにコピーしました')
                }}
              >
                <Copy className="h-4 w-4 mr-2" />
                コピー
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="w-full sm:w-auto"
                onClick={() => {
                  const blob = new Blob([currentSummary.summary], { type: 'text/plain;charset=utf-8' })
                  const url = URL.createObjectURL(blob)
                  const link = document.createElement('a')
                  link.href = url
                  link.download = `まとめ_${activeSession?.title || 'session'}_${new Date().toISOString().split('T')[0]}.txt`
                  document.body.appendChild(link)
                  link.click()
                  document.body.removeChild(link)
                  URL.revokeObjectURL(url)
                }}
              >
                <Download className="h-4 w-4 mr-2" />
                ダウンロード
              </Button>
              <Button
                variant="default"
                size="sm"
                className="w-full sm:w-auto"
                onClick={() => setShowSummary(false)}
              >
                閉じる
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}